import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

/**
 * Footer items are a [label, href] tuple. Internal hrefs start with `/` and
 * go through react-router's <Link> so SPA navigation is preserved; external
 * ones (mailto:, https:) are rendered as plain <a>. A null href falls back to
 * `#` and is only used for labels we haven't built yet (kept visible so the
 * footer layout doesn't collapse, but clearly marked via `aria-disabled`).
 */
type FooterItem = [label: string, href: string | null];

const productItems: FooterItem[] = [
  ['Evaluate', '/evaluate'],
  ['Generate', '/generate'],
  ['Rubric grading', '/rubric'],
  ['Pricing', '/pricing'],
];

const companyItems: FooterItem[] = [
  ['Privacy', '/privacy'],
  ['Terms', '/terms'],
  ['Refund policy', '/refund'],
  ['Contact', '/contact'],
];

const supportItems: FooterItem[] = [
  ['Help', 'mailto:support@gradelab.club'],
  ['Security', 'mailto:security@gradelab.club'],
  // Not built yet — keep hidden rather than showing dead links.
];

export default function Footer() {
  return (
    <footer className="border-t border-ink-200 bg-white">
      <div className="container-wide py-10 grid gap-8 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-2 font-semibold text-ink-900">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white grid place-items-center">
              <Sparkles className="w-4 h-4" />
            </span>
            GradeLab
          </div>
          <p className="mt-3 text-sm text-ink-500">AI-powered grading, generation & improvement for academic and professional reports.</p>
        </div>
        <FooterCol title="Product" items={productItems} />
        <FooterCol title="Company" items={companyItems} />
        <FooterCol title="Support" items={supportItems} />
      </div>
      <div className="border-t border-ink-100 py-4 text-center text-xs text-ink-500">
        © {new Date().getFullYear()} GradeLab. All rights reserved.
        <span className="mx-2 text-ink-300">·</span>
        <BuildTag />
      </div>
    </footer>
  );
}

/**
 * Visible build identifier — renders as e.g. "v0.1.0 · 3fa91c2 · 2026-04-18".
 * The three globals are inlined by Vite at build time (vite.config.ts), so
 * this is effectively free at runtime and confirms which bundle is live.
 * A `title` attribute carries the full ISO timestamp on hover for precision.
 */
function BuildTag() {
  const builtDate = __BUILD_TIME__.slice(0, 10); // YYYY-MM-DD
  return (
    <span className="font-mono text-ink-400" title={`Built ${__BUILD_TIME__}`}>
      v{__APP_VERSION__} · {__COMMIT_SHA__} · {builtDate}
    </span>
  );
}

function FooterCol({ title, items }: { title: string; items: FooterItem[] }) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-ink-900">{title}</h4>
      <ul className="mt-3 space-y-2 text-sm text-ink-500">
        {items.map(([label, href]) => (
          <li key={label}>{renderLink(label, href)}</li>
        ))}
      </ul>
    </div>
  );
}

function renderLink(label: string, href: string | null) {
  if (!href) {
    // Placeholder slot — rendered but not clickable so screen readers skip it.
    return <span className="text-ink-300" aria-disabled="true">{label}</span>;
  }
  const className = 'hover:text-ink-800 transition-colors';
  // mailto: and external http(s): bypass react-router; everything internal
  // uses <Link> to keep SPA navigation + preserve scroll-restoration.
  if (href.startsWith('mailto:') || /^https?:\/\//.test(href)) {
    return <a className={className} href={href}>{label}</a>;
  }
  return <Link className={className} to={href}>{label}</Link>;
}
